import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Download, FileText, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SpecificationsPage() {
  const [specsData, setSpecsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSpecs = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/specifications`);
        setSpecsData(response.data);
      } catch (error) {
        console.error('Error fetching specifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSpecs();
  }, []);

  const handleDownloadPDF = (productId) => {
    window.open(`${API_URL}/api/products/${productId}/pdf`, '_blank');
  };

  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="specifications-page">
      {/* Hero */}
      <section className="py-24 lg:py-32 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">TECHNICAL LIBRARY</p>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-6">
              SPECIFICATIONS
            </h1>
            <p className="text-stone/60 max-w-2xl text-lg">
              Complete technical documentation for the GEO product family. Download spec sheets, review comparison data, and reference applicable design standards.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Spec Sheets Download */}
      <section className="py-16 lg:py-24 section-light" data-testid="downloads-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">PRODUCT DATA SHEETS</p>
            <h2 className="heading-display text-charcoal text-4xl">
              DOWNLOAD SPECIFICATIONS
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { id: 'geobar-b', name: 'GEObar B', type: 'BFRP Rebar' },
              { id: 'geobar-fg', name: 'GEObar FG', type: 'GFRP Rebar' },
              { id: 'geofabric', name: 'GEOfabric', type: 'Basalt Mesh' }
            ].map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-white border border-stone/20 hover:border-bronze transition-colors"
              >
                <FileText size={32} className="text-bronze mb-4" />
                <h3 className="heading-section text-charcoal text-xl mb-1">{product.name}</h3>
                <p className="text-charcoal/60 text-sm mb-6">{product.type} Specification Sheet</p>
                <button
                  onClick={() => handleDownloadPDF(product.id)}
                  className="inline-flex items-center text-bronze text-sm font-medium hover:gap-3 gap-2 transition-all"
                  data-testid={`download-${product.id}-spec`}
                >
                  Download PDF <Download size={16} />
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 lg:py-24" data-testid="comparison-table-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">PERFORMANCE COMPARISON</p>
            <h2 className="heading-display text-stone text-4xl">
              PRODUCT COMPARISON
            </h2>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-2 border-bronze border-t-transparent animate-spin" />
            </div>
          ) : specsData ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="overflow-x-auto"
            >
              <table className="spec-table" data-testid="full-comparison-table">
                <thead>
                  <tr>
                    <th>Property</th>
                    <th>GEObar B (BFRP)</th>
                    <th>GEObar FG (GFRP)</th>
                    <th>GEOfabric</th>
                    <th>Grade 60 Steel</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Tensile Strength</td>
                    <td className="text-bronze">{specsData.products[0]?.specs?.tensile_strength}</td>
                    <td>{specsData.products[1]?.specs?.tensile_strength}</td>
                    <td>{specsData.products[2]?.specs?.tensile_strength}</td>
                    <td>{specsData.steel_comparison?.tensile_strength}</td>
                  </tr>
                  <tr>
                    <td>Weight per Foot</td>
                    <td>{specsData.products[0]?.specs?.weight_per_foot}</td>
                    <td>{specsData.products[1]?.specs?.weight_per_foot}</td>
                    <td>Varies</td>
                    <td>{specsData.steel_comparison?.weight_per_foot}</td>
                  </tr>
                  <tr>
                    <td>Modulus of Elasticity</td>
                    <td>{specsData.products[0]?.specs?.modulus_of_elasticity}</td>
                    <td>{specsData.products[1]?.specs?.modulus_of_elasticity}</td>
                    <td>—</td>
                    <td>{specsData.steel_comparison?.modulus_of_elasticity}</td>
                  </tr>
                  <tr>
                    <td>Corrosion Resistance</td>
                    <td className="text-bronze">{specsData.products[0]?.specs?.corrosion_resistance}</td>
                    <td className="text-bronze">{specsData.products[1]?.specs?.corrosion_resistance}</td>
                    <td className="text-bronze">Excellent</td>
                    <td className="text-stone/40">{specsData.steel_comparison?.corrosion_resistance}</td>
                  </tr>
                  <tr>
                    <td>Temperature Range</td>
                    <td>{specsData.products[0]?.specs?.temperature_range}</td>
                    <td>{specsData.products[1]?.specs?.temperature_range}</td>
                    <td>High</td>
                    <td>{specsData.steel_comparison?.temperature_range}</td>
                  </tr>
                  <tr>
                    <td>Electrical</td>
                    <td>Non-conductive</td>
                    <td>Non-conductive</td>
                    <td>{specsData.products[2]?.specs?.conductivity}</td>
                    <td>Conductive</td>
                  </tr>
                </tbody>
              </table>
            </motion.div>
          ) : null}
        </div>
      </section>

      {/* Design Standards */}
      <section className="py-16 lg:py-24 bg-surface-dark" data-testid="standards-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">REFERENCE DOCUMENTS</p>
            <h2 className="heading-display text-stone text-4xl">
              DESIGN STANDARDS
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {specsData?.standards?.map((standard, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-6 bg-charcoal border border-border-dark"
              >
                <span className="text-mono text-bronze text-sm">{standard.code}</span>
                <p className="text-stone/70 text-sm mt-2">{standard.description}</p>
              </motion.div>
            )) || (
              <>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="p-6 bg-charcoal border border-border-dark"
                >
                  <span className="text-mono text-bronze text-sm">ACI 440.1R-15</span>
                  <p className="text-stone/70 text-sm mt-2">Guide for the Design and Construction of Structural Concrete Reinforced with FRP Bars</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 }}
                  className="p-6 bg-charcoal border border-border-dark"
                >
                  <span className="text-mono text-bronze text-sm">ACI 440.11-22</span>
                  <p className="text-stone/70 text-sm mt-2">Building Code Requirements for Structural Concrete Reinforced with GFRP Bars</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                  className="p-6 bg-charcoal border border-border-dark"
                >
                  <span className="text-mono text-bronze text-sm">ASTM D7205</span>
                  <p className="text-stone/70 text-sm mt-2">Standard Test Method for Tensile Properties of FRP Composites</p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 }}
                  className="p-6 bg-charcoal border border-border-dark"
                >
                  <span className="text-mono text-bronze text-sm">ASTM D7957</span>
                  <p className="text-stone/70 text-sm mt-2">Standard Specification for Solid Round GFRP Bars for Concrete Reinforcement</p>
                </motion.div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Installation Notes */}
      <section className="py-16 lg:py-24" data-testid="installation-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <p className="text-bronze text-mono text-sm tracking-widest mb-4">FIELD GUIDANCE</p>
            <h2 className="heading-display text-stone text-4xl">
              INSTALLATION NOTES
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Cutting',
                content: 'FRP rebar can be cut with standard abrasive cutoff wheels, diamond blades, or carbide-tipped saws. Avoid standard steel cutting tools. Always wear appropriate PPE including eye protection and dust mask.'
              },
              {
                title: 'Handling',
                content: 'FRP rebar is lightweight (approximately 1/4 the weight of steel) but should be handled carefully to avoid surface damage. Store on flat surfaces away from direct UV exposure until installation.'
              },
              {
                title: 'Tie Wire',
                content: 'Use coated wire ties, nylon zip ties, or plastic clips. Avoid bare metal tie wire in direct contact with FRP to prevent galvanic concerns and surface damage.'
              }
            ].map((note, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 border border-border-dark bg-surface-dark"
              >
                <h3 className="heading-section text-stone text-xl mb-4">{note.title}</h3>
                <p className="text-stone/60 text-sm leading-relaxed">{note.content}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-surface-dark border-t border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-display text-stone text-3xl md:text-4xl mb-6">
              NEED ADDITIONAL TECHNICAL DATA?
            </h2>
            <p className="text-stone/60 max-w-xl mx-auto mb-8">
              Contact our technical team for detailed engineering support, custom specifications, or project-specific recommendations.
            </p>
            <Link 
              to="/contact" 
              className="btn btn-primary"
              data-testid="specs-cta-btn"
            >
              Contact Technical Team
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
